// UTM parameter capture, persistence, and passthrough for ad landing pages

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid', // Google Ads click id — needed for closed-loop conversion import
  'fbclid', // Meta click id
  'ref',
  'campaign',
] as const;
const STORAGE_KEY = 'pulsemd_utm';

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  ref?: string;
  campaign?: string;
}

// The acquisition columns on public.users. `ref`/`campaign` are intentionally
// excluded — `ref` feeds the physician-referral (NPI) flow, not ad attribution.
const ACQUISITION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const;

/**
 * Acquisition fields to persist on the user row at first profile creation.
 * Reads the UTM captured on the landing page (sessionStorage) and returns only
 * the keys that map to `users` columns and are actually set. Spread into the
 * user insert so every signup — email or Google OAuth — is attributable.
 */
export function getAcquisitionFields(): Partial<Record<(typeof ACQUISITION_KEYS)[number], string>> {
  const utm = getPersistedUTM();
  const out: Partial<Record<(typeof ACQUISITION_KEYS)[number], string>> = {};
  for (const key of ACQUISITION_KEYS) {
    const value = utm[key];
    if (value) out[key] = value;
  }
  return out;
}

/** Extract UTM params from current URL */
export function captureUTMFromURL(): UTMParams {
  const params = new URLSearchParams(window.location.search);
  const utm: UTMParams = {};

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
    }
  }

  return utm;
}

/** Save UTM params to sessionStorage (fresh per session) */
export function persistUTM(utm: UTMParams): void {
  if (Object.keys(utm).length > 0) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
  }
}

/** Retrieve saved UTM params */
export function getPersistedUTM(): UTMParams {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/** Capture UTM from URL and persist — call on landing page mount */
export function captureAndPersistUTM(): UTMParams {
  const utm = captureUTMFromURL();
  persistUTM(utm);
  return utm;
}

/** Build signup URL with UTM params, quiz ID, and pre-fill data */
export function buildSignupURL(quizSubmissionId?: string, name?: string, email?: string): string {
  const utm = getPersistedUTM();
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(utm)) {
    if (value) params.set(key, value);
  }

  if (quizSubmissionId) {
    params.set('quiz_id', quizSubmissionId);
  }
  if (name) {
    params.set('name', name);
  }
  if (email) {
    params.set('email', email);
  }

  const query = params.toString();
  return `/auth/signup${query ? `?${query}` : ''}`;
}
